import React, { useEffect, useState } from 'react';
import { usePageTitle } from '@app/hooks';
import { useStore } from 'effector-react';
import { $client, $computedLicence, $quotaUsage } from '@pages/Client/store/states';
import { openModal } from '@modals/store/events';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { Button, Classes, Intent, Spinner } from '@blueprintjs/core';
import DevText from '@components/Text';
import { fetchClientTeamMembers, fetchClientTeamParticipationList, removeClientTeamMember } from './store/effects';
import { resetClientTeamMembers, resetClientTeamParticipationList } from './store/events';
import { $clientTeamMembers, $clientTeamParticipationList } from './store/states';
import UserSpaceListItem from './components/UserSpaceListItem';
import { imageHashPreview } from '@data/consts';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import UserSpaceParticipationListItem from './components/UserSpaceParticipationListItem';
import AddUserSpaceMemberModal, { AddUserSpaceMemberModalProps } from './modals/AddUserSpaceMemberModal';
import { ToastUtils } from '@data/utils';
import { clientTeamMembersApi } from './store/api';
import { QuotaType } from 'dy-frontend-http-repository/lib/data/enums';

const ClientTeams: React.FC = () => {
    usePageTitle('Client Teams');

    const client = useStore($client);
    const computedLicence = useStore($computedLicence);
    const quotaUsage = useStore($quotaUsage);
    const teamMembers = useStore($clientTeamMembers);
    const teamParticipationList = useStore($clientTeamParticipationList);

    const [removingUserId, setRemovingUserId] = useState<ID | null>(null);

    useEffect(() => {
        if (!client) {
            return;
        }

        fetchClientTeamMembers(client.id).catch(console.error);
        fetchClientTeamParticipationList({ participant_user_id: client.id, is_suspend_exclude: '0' }).catch(
            console.error
        );
    }, [client?.id]);

    useEffect(() => {
        return () => {
            resetClientTeamMembers();
            resetClientTeamParticipationList();
        };
    }, []);

    let currentMembers = 0;
    let totalMembers = 0;
    if (computedLicence && quotaUsage) {
        currentMembers = quotaUsage.getValue(QuotaType.USER_SPACE_MEMBER_COUNT);
        totalMembers = computedLicence.getQuotaValue(QuotaType.USER_SPACE_MEMBER_COUNT);
    }

    const handleClientTeamMemberRemove = async (userSpaceId: ID, participantUserId: ID) => {
        if (client === null) {
            return;
        }

        setRemovingUserId(participantUserId);

        try {
            // Remove member
            await removeClientTeamMember({
                userSpaceId: userSpaceId,
                participantUserId: participantUserId,
            });

            // Update state
            clientTeamMembersApi.remove({ userId: client.id });

            ToastUtils.showToast({
                message: 'User successfully removed from the team',
                intent: Intent.SUCCESS,
            });
        } catch (e) {
            console.error(e);
        } finally {
            setRemovingUserId(null);
        }
    };

    if (!client) {
        return null;
    }

    const renderTeamMembersCounter = () => {
        const isLoading = quotaUsage === null || computedLicence === null;

        return (
            <DevText muted className={isLoading ? Classes.SKELETON : ''}>
                Members: {currentMembers} / {totalMembers}
            </DevText>
        );
    };

    const renderAddUserSpaceMemberButton = () => {
        const isButtonDisabled = currentMembers >= totalMembers;

        return (
            <Button
                disabled={isButtonDisabled}
                icon="new-person"
                className="ml-1"
                intent={Intent.PRIMARY}
                onClick={() =>
                    openModal<AddUserSpaceMemberModalProps>({
                        ModalComponent: AddUserSpaceMemberModal,
                        data: {
                            userSpaceId: client.id,
                        },
                    })
                }
            >
                Add member
            </Button>
        );
    };

    const renderHeader = () => {
        return (
            <Flex justify="space-between" align="center" className="mb-1">
                <Heading type="h3">
                    {client.first_name} {client.last_name}'s team
                </Heading>

                <Flex direction="row" align="center">
                    {renderTeamMembersCounter()}
                    {renderAddUserSpaceMemberButton()}
                </Flex>
            </Flex>
        );
    };

    const renderTeamMembers = () => {
        if (teamMembers === null) {
            return (
                <Flex align="center" justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // First one is always the owner
        let ownerAvatarSrc: string | null = null;
        if (client.image_hash) {
            ownerAvatarSrc = imageHashPreview.userImage(client.image_hash, ImageHashPreviewSize.SM);
        }

        const members = [
            <UserSpaceParticipationListItem
                isOwner
                isSuspended={true}
                addedAt=""
                className="mb-1"
                key={`user-participant-${client.id}`}
                userId={client.id}
                userImageSrc={ownerAvatarSrc}
                userFullName={`${client.first_name} ${client.last_name}`}
            />,
        ];

        // Add other members
        teamMembers.items.forEach((member) => {
            let avatarSrc: string | null = null;

            if (member.user.image_hash) {
                avatarSrc = imageHashPreview.userImage(member.user.image_hash, ImageHashPreviewSize.SM);
            }

            members.push(
                <UserSpaceParticipationListItem
                    isOwner={false}
                    isSuspended={member.suspended_at !== null}
                    isRemoving={removingUserId === member.user.id}
                    addedAt={member.added_at}
                    userId={member.user.id}
                    className="mb-1"
                    key={`user-participant-${member.user.id}`}
                    userFullName={`${member.user.first_name} ${member.user.last_name}`}
                    userImageSrc={avatarSrc}
                    onRemove={() => handleClientTeamMemberRemove(client.id, member.user.id)}
                />
            );
        });

        return members;
    };

    const renderTeamParticipationList = () => {
        if (teamParticipationList === null) {
            return (
                <Flex align="center" justify="center">
                    <Spinner />
                </Flex>
            );
        }

        if (teamParticipationList.items.length === 0) {
            return <DevText muted>Client do not participate in any of the teams</DevText>;
        }

        return teamParticipationList.items.map((t) => {
            let avatarSrc: string | null = null;
            if (t.image_hash) {
                avatarSrc = imageHashPreview.userImage(t.image_hash, ImageHashPreviewSize.SM);
            }

            return (
                <UserSpaceListItem
                    className="mb-1"
                    key={`user-space-${t.id}`}
                    userSpaceId={t.id}
                    imageSrc={avatarSrc}
                    title={t.title}
                    companyName={t.owner.company_name}
                />
            );
        });
    };

    return (
        <div>
            <div className="mb-3">
                {renderHeader()}
                {renderTeamMembers()}
            </div>

            <div className="mb-3">
                <Flex justify="space-between" align="center" className="mb-1">
                    <Heading type="h3">Participant in other teams</Heading>
                </Flex>

                {renderTeamParticipationList()}
            </div>
        </div>
    );
};

export default ClientTeams;
